import {HeaderState} from './HeaderState'
import {Box} from '@primer/react'
import {TaskListStatus} from '../shared/TaskListStatus'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {StickyHeaderTitle} from './StickyHeaderTitle'
import {HeaderMenu, type HeaderMenuBaseProps} from './HeaderMenu'
import {Suspense, useMemo, useRef} from 'react'
import {ContentWrapper} from '../ContentWrapper'
import {HeaderIssueType} from './HeaderIssueType'
import type {OptionConfig} from '../OptionConfig'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {LazyLinkedPullRequests} from './LinkedPullRequests'
import {RepositoryPill} from './RepositoryPill'
import {useFragment} from 'react-relay'
import {graphql} from 'relay-runtime'
import type {HeaderMetadata$key} from './__generated__/HeaderMetadata.graphql'
import {TrackedBy} from './TrackedBy'

export type HeaderMetadataProps = {
  headerMetadataKey: HeaderMetadata$key
  optionConfig: OptionConfig
  isSticky: boolean
  stickyStyles: BetterSystemStyleObject
} & HeaderMenuBaseProps

export function HeaderMetadata({headerMetadataKey, optionConfig, isSticky, metadataPaneTrigger}: HeaderMetadataProps) {
  const {showRepositoryPill, innerSx} = optionConfig

  const headerData = useFragment(
    graphql`
      fragment HeaderMetadata on Issue {
        id
        url
        issueType {
          id
          name
        }
        ...HeaderMenu
        ...HeaderState
        ...StickyHeaderTitle
        ...RepositoryPill
        ...LinkedPullRequests
      }
    `,
    headerMetadataKey,
  )
  const {issueType} = headerData

  const headerContainerRef = useRef<HTMLDivElement>(null)
  const breakpoint = useContainerBreakpoint(headerContainerRef.current)

  const styles = useMemo(() => {
    const stickyProps = isSticky
      ? {
          position: 'sticky',
          top: 0,
        }
      : {}
    const sx: BetterSystemStyleObject = {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '56px',
      borderBottom: '1px solid',
      borderColor: isSticky ? 'border.default' : 'transparent',
      zIndex: 14,
      backgroundColor: isSticky ? 'canvas.default' : 'transparent',
      ...stickyProps,
    }

    return sx
  }, [isSticky])

  return (
    <>
      <Box
        ref={headerContainerRef}
        sx={{...styles}}
        // This class offsets the entire component while the notifications shelf is visible
        className={isSticky ? 'js-notification-shelf-offset-top' : undefined}
      >
        <ContentWrapper sx={{display: 'flex', alignItems: 'center', gap: 2, ...innerSx}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mr: 1}}>
            <HeaderState stateData={headerData} />
            {!isSticky && issueType && <HeaderIssueType name={issueType.name} />}
            <Box
              sx={{
                display: optionConfig.useViewportQueries
                  ? ['none', 'none', 'flex', 'flex']
                  : breakpoint(['none', 'none', 'flex', 'flex']),
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Suspense>
                <TrackedBy issueId={headerData.id} url={headerData.url} />
              </Suspense>
              <Suspense>
                <TaskListStatus issueId={headerData.id} />
              </Suspense>
            </Box>
          </Box>

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {isSticky && (
              <Box sx={{display: 'flex', ml: 1}}>
                <StickyHeaderTitle headerTitleData={headerData} scrollToTopOnClick={optionConfig.scrollToTopOnClick} />
              </Box>
            )}

            <Box
              sx={{
                display: optionConfig.useViewportQueries
                  ? ['none', 'none', 'flex', 'flex']
                  : breakpoint(['none', 'none', 'flex', 'flex']),
                flexDirection: 'row',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'nowrap',
                whiteSpace: 'nowrap',
              }}
            >
              {showRepositoryPill && <RepositoryPill sx={{maxWidth: '50%'}} repositoryPillData={headerData} />}
              <LazyLinkedPullRequests issueId={headerData.id} isSticky={isSticky} />
            </Box>
          </Box>
          {isSticky && (
            <Box>
              <HeaderMenu
                optionConfig={optionConfig}
                isSticky={isSticky}
                headerMenuData={headerData}
                metadataPaneTrigger={metadataPaneTrigger}
              />
            </Box>
          )}
          {metadataPaneTrigger}
        </ContentWrapper>
      </Box>

      <ContentWrapper
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          ...innerSx,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{width: '100%', borderBottom: '1px solid', borderBottomColor: 'border.subtle'}}>
          <Box
            sx={{
              display: optionConfig.useViewportQueries
                ? ['flex', 'flex', 'none', 'none']
                : breakpoint(['flex', 'flex', 'none', 'none']),
              alignItems: 'center',
              pb: 3,
              columnGap: 2,
              rowGap: 2,
              flexWrap: 'wrap',
            }}
          >
            {showRepositoryPill && <RepositoryPill sx={{maxWidth: '50%'}} repositoryPillData={headerData} />}
            <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
              <Suspense>
                <TrackedBy issueId={headerData.id} url={headerData.url} />
              </Suspense>
              <Suspense>
                <TaskListStatus issueId={headerData.id} />
              </Suspense>
              <LazyLinkedPullRequests issueId={headerData.id} isSticky={isSticky} />
            </Box>
          </Box>
        </Box>
      </ContentWrapper>
    </>
  )
}

try{ HeaderMetadata.displayName ||= 'HeaderMetadata' } catch {}